import { Box, ButtonBase, Divider, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { FavoriteBorderRounded, FavoriteRounded } from "@mui/icons-material";

import { HexTile, StatusBadge } from "../../../../components/ui";
import TagChip from "../../../../components/ui/TagChip";
import { ActiveModel } from "../active-model";
import { isOral, isVehicle } from "../library-filters";

// ----------------------------------------------------------------------

interface ActiveCardProps {
  item: ActiveModel;
  onOpen: (item: ActiveModel) => void;
  onToggleFavorite: (item: ActiveModel) => void;
  /** Linha compacta (mobile): hexágono + nome + mecanismo + concentração à direita */
  compact?: boolean;
}

const viaLabel = (item: ActiveModel) => (isVehicle(item) ? "Veículo" : isOral(item) ? "Oral" : "Tópico");

const concentrationShort = (item: ActiveModel): string => {
  const c = item.usualConcentration;
  if (!c || (!c.min && !c.max)) return "";
  const value = c.max || c.min;
  return `${value}${c.unit ? ` ${c.unit}` : ""}`;
};

export default function ActiveCard({ item, onOpen, onToggleFavorite, compact = false }: ActiveCardProps) {
  const theme = useTheme();

  if (compact) {
    const concentration = concentrationShort(item);
    return (
      <ButtonBase
        onClick={() => onOpen(item)}
        sx={{
          width: "100%",
          textAlign: "left",
          gap: "13px",
          p: "14px 16px",
          borderRadius: "15px",
          backgroundColor: "background.paper",
          border: `1.5px solid ${theme.palette.divider}`,
          alignItems: "center",
        }}
      >
        <HexTile label={item.name} size={36} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: "14.5px", fontWeight: 600, lineHeight: 1.3, color: "text.primary" }}>
            {item.name}
          </Typography>
          {item.mechanismAction && (
            <Typography noWrap sx={{ fontSize: "12.5px", color: theme.palette.brand.heroText }}>
              {item.mechanismAction}
            </Typography>
          )}
        </Box>
        {concentration && (
          <Typography sx={{ flexShrink: 0, fontSize: "13.5px", fontWeight: 600, color: "primary.main" }}>
            {concentration}
          </Typography>
        )}
      </ButtonBase>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "background.paper",
        border: `1.5px solid ${theme.palette.divider}`,
        borderRadius: "15px",
        transition: "all .15s",
        "&:hover": {
          borderColor: theme.palette.brand.creamStroke === "transparent" ? theme.palette.primary.main : theme.palette.brand.creamStroke,
          boxShadow: theme.customShadows.card,
        },
      }}
    >
      <ButtonBase
        onClick={() => onOpen(item)}
        sx={{ width: "100%", alignItems: "flex-start", textAlign: "left", p: "18px 20px", borderRadius: "15px", gap: "13px", flexDirection: "column" }}
      >
        <Box sx={{ width: "100%", display: "flex", gap: "13px", alignItems: "flex-start" }}>
          <HexTile label={item.name} size={40} />
          <Box sx={{ flex: 1, minWidth: 0, pr: 4 }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, lineHeight: 1.3, color: "text.primary" }}>
              {item.name}
            </Typography>
            <Box sx={{ mt: "5px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
              <StatusBadge status={viaLabel(item)} tone="neutral" />
            </Box>
            {item.classes && item.classes.length > 0 && (
              <Box sx={{ mt: "6px", display: "flex", gap: "5px", flexWrap: "wrap" }}>
                {item.classes.slice(0, 2).map((cls) => (
                  <TagChip key={cls} label={cls} />
                ))}
                {item.classes.length > 2 && (
                  <TagChip label={`+${item.classes.length - 2}`} />
                )}
              </Box>
            )}
            {item.mechanismAction && (
              <Typography
                sx={{
                  m: "12px 0 0",
                  fontSize: "12.5px",
                  lineHeight: 1.5,
                  color: theme.palette.brand.heroText,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {item.mechanismAction}
              </Typography>
            )}
          </Box>
        </Box>

        {item.usualConcentration && (item.usualConcentration.min || item.usualConcentration.max) && (
          <Box sx={{ width: "100%", mt: "4px" }}>
            <Divider sx={{ mb: "10px" }} />
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
              <Typography
                sx={{
                  fontSize: "10.5px",
                  fontWeight: 500,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: theme.palette.brand.heroText,
                }}
              >
                Concentração usual
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "primary.main",
                }}
              >
                {[item.usualConcentration.min, item.usualConcentration.max]
                  .filter(Boolean)
                  .join(" – ")}
                {item.usualConcentration.unit ? ` ${item.usualConcentration.unit}` : ""}
              </Typography>
            </Box>
          </Box>
        )}
      </ButtonBase>

      {!isVehicle(item) && (
        <Tooltip title={item.favorite ? "Remover dos favoritos" : "Favoritar"} placement="top">
          <IconButton
            aria-label="Favoritar"
            onClick={(event) => {
              event.stopPropagation();
              onToggleFavorite(item);
            }}
            sx={{ position: "absolute", top: 12, right: 12, width: 34, height: 34, borderRadius: "9px" }}
          >
            {item.favorite ? (
              <FavoriteRounded sx={{ fontSize: 19, color: "primary.main" }} />
            ) : (
              <FavoriteBorderRounded sx={{ fontSize: 19, color: theme.palette.brand.creamStroke === "transparent" ? "text.disabled" : theme.palette.brand.creamStroke }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
